import { ChangeEventHandler, FC, Fragment } from "react";
import { Grid, makeStyles, createStyles, Box } from "@material-ui/core";
import { GridSize } from "@material-ui/core/Grid";
import { ChangeNames } from "../../clients/FirebaseClient/types";
import { useFormModel } from "../core/FormModelHelpes";
import { LabledTextField } from "../core/LabledTextField";
import { Select, MenuItem } from "../core/Select";
import { Colors } from "../../styles";
import InputLabel from "@material-ui/core/InputLabel";
import { AddressCompletionField } from "../core/AddressCompletionField";

const useStyles = makeStyles(() =>
    createStyles({
        container: {
            display: "flex",
            flexWrap: "wrap",
            flexDirection: "column",
            padding: 0,
            borderRadius: 8,
            marginTop: 0,
            marginBottom: 24,
            fontFamily: "innogyWEB",
            fontStretch: "normal",
            fontStyle: "normal",
            letterSpacing: "normal",
            color: Colors.anthracite,
        },
        title: {
            margin: 0,
            fontSize: 37,
            fontWeight: 500,
            height: 43,
            marginBottom: 24,
        },
        subTitle: {
            margin: 0,
            fontSize: 24,
            fontWeight: 500,
            lineHeight: 1.45,
            height: 32,
            marginBottom: 8,
        },
        text: {
            margin: 0,
            fontSize: 22,
            fontWeight: 300,
            lineHeight: 1.27,
            marginBottom: 24,
        },
        labledTextfieldLabel: {
            marginLeft: 20,
            height: 28,
            fontSize: 22,
            fontWeight: 300,
            fontStretch: "normal",
            fontStyle: "normal",
            fontFamily: "innogyWEB",
            lineHeight: 1.27,
            letterSpacing: "normal",
            color: Colors.anthracite,
        },
        disclaimerContainer: {
            fontFamily: "innogyWEB",
            fontStretch: "normal",
            fontStyle: "normal",
            letterSpacing: "normal",
            color: Colors.anthracite50,
            fontSize: 18,
        },
    }),
);

type PathProxyField = {
    key: string;
    label: string;
    path?: string;
    width: number;
};

type SalutationField = {
    label: string;
    width: number;
};

type FillerField = {
    width: number;
};

type Field = {
    pathProxyField?: PathProxyField;
    salutationField?: SalutationField;
    fillerField?: FillerField;
};

type Fields = {
    [x: string]: Field;
};

type Salutation = {
    key: string;
    label: string;
};

const salutations: Salutation[] = [
    { key: "company", label: "Firma" },
    { key: "flatSharingCommunity", label: "Wohngemeinschaft" },
    { key: "buildingOwnerAssociation", label: "Bauherrengemeinschaft" },
    { key: "heirCommunity", label: "Erbengemeinschaft" },
    { key: "publicOfficials", label: "Träger öffentlicher Belange" },
];

export const SystemConstructorForm: FC = () => {
    const classes = useStyles();

    const { getFieldProps, getFieldValue, setFieldValue, values, setValues } =
      useFormModel<ChangeNames.SYSTEM_CONSTRUCTOR>();

  const legalFields: Fields = {
        name: {
            pathProxyField: {
                key: "Name",
                label: "Name *",
                path: "systemConstructor.value.legal.name",
                width: 12,
            },
        },
        plz: {
            pathProxyField: {
                key: "PLZ",
                label: "PLZ *",
                path: "systemConstructor.value.legal.address.zipCode",
                width: 6,
            },
        },
        ort: {
            pathProxyField: {
                key: "Ort",
                label: "Ort *",
                path: "systemConstructor.value.legal.address.city",
                width: 6,
            },
        },
        strasse: {
            pathProxyField: {
                key: "Strasse",
                label: "Straße *",
                path: "systemConstructor.value.legal.address.street",
                width: 6,
            },
        },
        hausnummer: {
            pathProxyField: {
                key: "Hausnummer",
                label: "Hausnummer *",
                path: "systemConstructor.value.legal.address.houseNumber",
                width: 6,
            },
        },
        email: {
            pathProxyField: {
                key: "Email",
                label: "E-Mail-Adresse*",
                path: "systemConstructor.value.legal.email",
                width: 6,
            },
        },
        telefon: {
            pathProxyField: {
                key: "Telefon",
                label: "Telefonnummer *",
                path: "systemConstructor.value.legal.phoneNumber",
                width: 6,
            },
        },
    };

    const fields = {
        salutation: {
            salutationField: {
                label: "Anrede *",
                width: 6,
            },
        },
        salutationFiller: {
            fillerField: { width: 6 },
        },
      legal: legalFields,
    };

  const getSalutationFromChangeSet = (): string =>  Object.entries(values.systemConstructor.value.salutation).find(it => it[1])?.[0] || "";


  const setSalutation: ChangeEventHandler<HTMLInputElement> = (e) => {
    const entries = Object.keys(values.systemConstructor.value.salutation).map(it => [it, it === e.target.value])
    setValues(v => {
      v.systemConstructor.value.salutation = Object.fromEntries(entries)
    })
  };

    const renderField = (field: Field): JSX.Element => {
        const pathProxyField = field.pathProxyField;
        const fillerField = field.fillerField;
        const salutationField = field.salutationField;

        if (pathProxyField && pathProxyField.path) {
            if (["PLZ", "Ort", "Strasse"].includes(pathProxyField.key)) {
                const complete =
                    pathProxyField.key === "PLZ"
                        ? "zipCode"
                        : pathProxyField.key === "Ort"
                        ? "city"
                        : "street";
              
                return (
                    <Grid
                        item
                        md={pathProxyField.width as GridSize}
                        sm={12}
                        xs={12}
                        key={pathProxyField.key}
                    >
                        <AddressCompletionField
                            label={pathProxyField.label}
                            zipCode={values.systemConstructor.value.legal?.address?.zipCode?.value || ""}
                            city={values.systemConstructor.value.legal?.address?.city?.value || ""}
                            street={values.systemConstructor.value.legal?.address?.street?.value}
                            complete={complete}
                            error={!!getFieldProps(`${pathProxyField.path}.error`).value}
                            onChange={(newValue) =>
                                setFieldValue(`${pathProxyField.path}.value`, newValue)
                            }
                        />
                    </Grid>
                );
            } else {
                const maxLength = new Map<string, number>(
                    Object.entries({
                        Vorname: 35,
                        Nachname: 35,
                        Name: 70,
                    }),
                ).get(pathProxyField.key);
                return (
                    <Grid
                        item
                        md={pathProxyField.width as GridSize}
                        sm={12}
                        xs={12}
                        key={pathProxyField.key}
                    >
                        <LabledTextField
                            data-testid={pathProxyField.path}
                            label={pathProxyField.label}
                            style={{ width: "100%", flex: 1 }}
                            margin="normal"
                            variant="outlined"
                            inputProps={{ maxLength }}
                            error={!!getFieldValue(`${pathProxyField.path}.error`)}
                            helperText={getFieldValue(`${pathProxyField.path}.error`)}
                            {...getFieldProps(`${pathProxyField.path}.value`)}
                        />
                    </Grid>
                );
            }
        }
        if (salutationField) {
            return (
                <Grid item md={6 as GridSize} sm={12} xs={12} style={{ height: 56 }}>
                    <InputLabel className={classes.labledTextfieldLabel}>Anrede *</InputLabel>
                    <Select
                        style={{ width: "100%", flex: 1 }}
                        value={getSalutationFromChangeSet()}
                        onChange={setSalutation}
                        error={
                          (values.systemConstructor.error?.indexOf("salutation") || -1) >= 0
                        }
                    >
                        {[{ key: "", label: "Bitte wählen" }]
                            .concat(salutations)
                            .map((salutation) => (
                                <MenuItem value={salutation.key} key={salutation.key}>
                                    {salutation.label}
                                </MenuItem>
                            ))}
                    </Select>
                </Grid>
            );
        }
        if (fillerField) {
            return <Grid item md={fillerField.width as GridSize} sm={12} xs={12} />;
        }

        return <Fragment />;
    };

    const renderFields = (fields: Fields): JSX.Element => {
        return (
            <Fragment>{Object.keys(fields).map((field) => renderField(fields[field]))}</Fragment>
        );
    };

    return (
        <Fragment>
            <div className={classes.container}>
                <div className={classes.subTitle}>Errichter der Anlage (eingetragener Elektrofachbetrieb)</div>
                <div className={classes.text}>
                    Hier erfassen Sie die Daten Ihres eingetragenen Elektrofachbetriebs.
                </div>
                <Grid container spacing={2}>
                    {renderField(fields.salutation)}
                  <Grid item md={6 as GridSize} sm={12} xs={12}>
                    <Box height={88} />
                    </Grid>
                    {renderFields(legalFields)}
                </Grid>
                <div className={classes.disclaimerContainer}>
                    <p>* Mit Sternchen gekennzeichnete Felder sind Pflichtfelder.</p>
                </div>
            </div>
        </Fragment>
    );
};
